// Распознавание намерения до запуска. Экраны 03, 03б и 04.
//
// Ручка не создаёт запуск — и это не деталь реализации, а гарантия правила
// «уточнения не списываются»: нет запуска, нет исхода, нечему списаться.
//
// Она же отвечает на главный вопрос развилки: показать шит подтверждения или
// объявить списание строкой. Решает сервер, потому что правило зависит от
// остатка и настроек подтверждения; клиенту остаётся отрисовать пришедшее.

import type { RiskArea, ScenarioId, UnitId } from '@smyslika/core'

import type { Client } from './client.ts'

/** Один вопрос уточнения. Пустой `options` — ответ свободный. */
export interface Clarification {
  question: string
  options: string[]
}

/** Шит подтверждения целиком — экран 04. Собран на сервере. */
export interface ConfirmationSheet {
  scenario: ScenarioId
  unit: UnitId
  reason: 'detected_by_us' | 'unit_changed' | 'not_enough'
  label: string
  promise: string
  explain: string
  includes: string[]
  cost: number
  remainingBefore: number
  remainingAfter: number
  limit: number
  etaSeconds?: number
  action: string
  decline?: string
  skipLabel: string
  notice: string
}

export interface Intent {
  scenario: ScenarioId
  unit: UnitId
  remaining?: number
  limit?: number
  /**
   * Тема повышенного риска. Перечислением, а не строкой: с SMY-124 она живёт
   * в ядре, и `string` здесь означал бы, что клиент про неё ничего не знает.
   */
  risk: RiskArea
  questions: Clarification[]
  /** Задачу определили мы, а не пользователь. Вход правила показа экрана 04. */
  detectedByUs: boolean
  /** Запустится ли сценарий на самом деле. */
  available: boolean
  /** Сценарий понижен из более дорогого — экран обязан это назвать. */
  downgradedFrom?: ScenarioId
  /** Есть — показываем экран 04. */
  confirm?: ConfirmationSheet
  /** Есть — та же правда одной строкой, экран 03б. */
  notice?: string
  /**
   * Ключ решения классификатора. SMY-124 · P8 Э8.6
   *
   * Клиент не разбирает его и не показывает: единственное, что с ним делают, —
   * возвращают в `POST /v1/runs`. По этой ссылке сервер узнаёт, совпал ли
   * запуск с тем, что он понял, — а без неё расхождение не видно ничем.
   *
   * Пусто — записи не случилось. Запуск при этом обычный, просто несвязанный.
   */
  intentId?: string
}

export interface IntentInput {
  text: string
  /** Сценарий, выбранный карточкой. Пусто — свободный чат. */
  chosen?: ScenarioId
  fileIds?: string[]
}

export const createIntent = (client: Client) => ({
  /**
   * `signal` — срок экрана (`withDeadline`): повисшее распознавание держало бы
   * «Читаю задачу» и закрытую отправку, пока системе не надоест (ворота ревью
   * 5, ux-states-checker).
   */
  detect: (input: IntentInput, signal?: AbortSignal) =>
    client.request<Intent>('/v1/intent', { method: 'POST', body: input, ...(signal != null ? { signal } : {}) }),
})

export type IntentApi = ReturnType<typeof createIntent>
